"use client";

import React, { useEffect, useRef, useState } from 'react';
import { Zap, Shield, Server, Crown, User, Activity, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Link } from '@/components/common/Link';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  icon: Zap,
  title: "Systematic Execution",
  description: "Trades are driven by a rules-based strategy process designed to maintain execution logic in under 100ms."
}, {
  icon: Shield,
  title: "Dynamic Risk Allocation",
  description: "Automated position sizing adjusts to your specific account equity and predefined risk parameters in real-time."
}, {
  icon: Server,
  title: "Professional Infrastructure",
  description: "Built on enterprise-grade servers with 99.99% uptime, guaranteeing your portfolio is always synchronized."
}];
const followers = [{
  id: 1,
  x: 20,
  delay: "0ms"
}, {
  id: 2,
  x: 50,
  delay: "150ms"
}, {
  id: 3,
  x: 80,
  delay: "300ms"
}];
export default function TheCopytradingEdge() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: graphicRef,
    isVisible: isGraphicVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="the-copytrading-edge" className="dark bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
            {}
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] md:w-[800px] md:h-[800px] bg-primary/10 rounded-full blur-[100px] md:blur-[150px] pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                    
                    {}
                    <div ref={textRef} className={`flex flex-col space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        <div className="space-y-4">
                            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
                                <Activity className="w-4 h-4" />
                                <span>The Imperium Engine</span>
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                                Technical <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">Superiority</span>
                            </h2>
                            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">Imperium Technologies is built around a structured strategy engine designed for low-latency execution, controlled risk logic, and consistent operational flow. The service is focused on process, synchronization and professional client access rather than emotional or discretionary decision-making.</p>
                        </div>

                        <div className="space-y-6 pt-4">
                            {features.map((feature, i) => <div key={i} data-index={i} className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-card border border-border flex items-center justify-center shadow-sm">
                                        <feature.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-foreground mb-1">{feature.title}</h3>
                                        <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                                    </div>
                                </div>)}
                        </div>

                        <div className="pt-6">
                            <Link to="/AccessPage">
                                
                            </Link>
                        </div>
                    </div>

                    {}
                    <div ref={graphicRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isGraphicVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        <Card className="relative w-full aspect-[4/3] sm:aspect-square lg:aspect-[4/3] bg-card/40 backdrop-blur-xl border-border/50 shadow-2xl overflow-hidden group">
                            {}
                            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-50" />
                            
                            {}
                            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <defs>
                                    <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                                        <feGaussianBlur stdDeviation="2" result="blur" />
                                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                                    </filter>
                                    <linearGradient id="lineGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.8" />
                                        <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0.2" />
                                    </linearGradient>
                                </defs>
                                
                                <path d="M50,25 C50,50 20,50 20,75" fill="none" stroke="url(#lineGrad)" strokeWidth="0.5" filter="url(#glow)" className="opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
                                <path d="M50,25 C50,50 50,50 50,75" fill="none" stroke="url(#lineGrad)" strokeWidth="0.5" filter="url(#glow)" className="opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
                                <path d="M50,25 C50,50 80,50 80,75" fill="none" stroke="url(#lineGrad)" strokeWidth="0.5" filter="url(#glow)" className="opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
                            </svg>

                            {}
                            <div className="absolute top-[25%] left-[50%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-10">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-primary/20 rounded-full blur-md animate-pulse" />
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-background border-2 border-primary flex items-center justify-center shadow-[0_0_15px_rgba(var(--color-primary),0.5)] relative z-10">
                                        <Crown className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                                    </div>
                                </div>
                                <span className="mt-3 text-sm font-semibold text-foreground bg-background/80 px-3 py-1 rounded-full border border-border backdrop-blur-sm">
                                    Master Account
                                </span>
                            </div>

                            {}
                            {followers.map((follower, i) => <div key={follower.id} data-index={i} className="absolute top-[75%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-10" style={{
              left: `${follower.x}%`
            }}>
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-card border border-border flex items-center justify-center shadow-lg group-hover:border-primary/50 transition-colors duration-500 relative" style={{
                transitionDelay: follower.delay
              }}>
                                        <User className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground group-hover:text-primary transition-colors duration-500" style={{
                  transitionDelay: follower.delay
                }} />
                                        {follower.id === 2}
                                    </div>
                                    <span className="mt-2 text-xs font-medium text-muted-foreground">
                                        Follower {follower.id}
                                    </span>
                                </div>)}

                            {}
                            <div className="absolute top-4 left-4 flex space-x-1.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-border/50" />
                                <div className="w-2.5 h-2.5 rounded-full bg-border/50" />
                                <div className="w-2.5 h-2.5 rounded-full bg-border/50" />
                            </div>
                            <div className="absolute bottom-4 right-4 flex items-center space-x-2 text-xs text-primary/80 font-mono">
                                <span className="relative flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                                </span>
                                <span>SYNC ACTIVE</span>
                            </div>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}